import { type ZodType } from 'zod';
import {
  DraftPhotoCleanupResponseSchema,
  DraftSaveResponseSchema,
  type DraftPhotoCleanupRequest,
  type DraftPhotoCleanupResponse,
  type DraftSaveInput,
  type DraftSaveResponse,
} from '@magimp/shared';
import { request } from '../client.js';

/**
 * @aiPurpose `POST /requests/draft` — first save of a never-persisted
 *   editable DRAFT. The store mints `clientToken` once on `openDraft`
 *   and replays it across every retry; the server's partial UNIQUE
 *   INDEX collapses duplicate POSTs to the same row. The response is
 *   the full saved row plus the `clientItemId → it-NNNN` map so the
 *   editor can re-baseline by canonical server id without losing track
 *   of items the user is still editing.
 */
export function createDraftRequest(input: DraftSaveInput): Promise<DraftSaveResponse> {
  return request<DraftSaveResponse>(
    '/requests/draft',
    {
      method: 'POST',
      body: JSON.stringify(input),
      headers: { 'content-type': 'application/json' },
    },
    {},
    DraftSaveResponseSchema as unknown as ZodType<DraftSaveResponse>,
  );
}

/**
 * @aiPurpose `PUT /requests/:id/draft` — last-write-wins update of a
 *   persisted DRAFT (Resolved Decision #14). `clientToken` is silently
 *   ignored on this path; the row id is the idempotency key. Server
 *   diffs items by `id` (existing) vs no-id (create) and removes any
 *   server-side item whose `id` is absent in the payload, then runs a
 *   non-blocking cleanup of the storage keys that fell out of the
 *   saved row.
 */
export function updateDraftRequest(id: string, input: DraftSaveInput): Promise<DraftSaveResponse> {
  return request<DraftSaveResponse>(
    `/requests/${encodeURIComponent(id)}/draft`,
    {
      method: 'PUT',
      body: JSON.stringify(input),
      headers: { 'content-type': 'application/json' },
    },
    {},
    DraftSaveResponseSchema as unknown as ZodType<DraftSaveResponse>,
  );
}

/**
 * @aiPurpose `POST /requests/draft/photo-cleanup` — discard cleanup
 *   for never-saved upload keys when the user closes the editor before
 *   any save landed. Authorization is conjunctive in the service
 *   (parsed key scope, per-actor namespace, no `RequestItem` reference).
 *   Best-effort: per-key failures surface in `failed` without aborting
 *   the batch so the UI can keep going on partial success.
 */
export function cleanupNeverSavedDraftPhotos(
  body: DraftPhotoCleanupRequest,
): Promise<DraftPhotoCleanupResponse> {
  return request<DraftPhotoCleanupResponse>(
    '/requests/draft/photo-cleanup',
    {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'content-type': 'application/json' },
    },
    {},
    DraftPhotoCleanupResponseSchema,
  );
}

/**
 * @aiPurpose `POST /requests/:id/draft/photo-cleanup` — discard cleanup
 *   for keys that were uploaded against a persisted draft-like row
 *   (`DRAFT | RETURN`) but never reached a save (the user uploaded a
 *   new photo, then chose Discard or delete). The server enforces row
 *   + draft-like-status auth before any per-key check, so a
 *   non-draft-like row collapses to `INVALID_TRANSITION`.
 */
export function cleanupPersistedDraftPhotos(
  id: string,
  body: DraftPhotoCleanupRequest,
): Promise<DraftPhotoCleanupResponse> {
  return request<DraftPhotoCleanupResponse>(
    `/requests/${encodeURIComponent(id)}/draft/photo-cleanup`,
    {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'content-type': 'application/json' },
    },
    {},
    DraftPhotoCleanupResponseSchema,
  );
}
